import { useCallback, useEffect, useState } from "react";

/**
 * The `beforeinstallprompt` event (Chromium-only, not in lib.dom yet).
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

const DISMISS_KEY = "backoffice.pwa.installDismissed";

const isStandalone = (): boolean =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari exposes navigator.standalone instead of display-mode.
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true);

/** Heuristic: iOS Safari never fires beforeinstallprompt; A2HS is manual. */
const isIosSafari = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && webkit;
};

export interface UsePwaInstallResult {
  /** Chromium captured a prompt we can fire. */
  canPrompt: boolean;
  /** iOS Safari: no programmatic prompt, show manual A2HS instructions. */
  isIos: boolean;
  /** Already running as an installed app — hide everything. */
  isInstalled: boolean;
  /** User dismissed our banner this device (persisted). */
  dismissed: boolean;
  /** Fire the native install prompt (Chromium). */
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
  /** Remember the dismissal so we don't nag. */
  dismiss: () => void;
}

export function usePwaInstall(): UsePwaInstallResult {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [installed, setInstalled] = useState<boolean>(isStandalone);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Prevent the mini-infobar; we render our own affordance instead.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    const mql = window.matchMedia("(display-mode: standalone)");
    const onModeChange = (e: MediaQueryListEvent) => setInstalled(e.matches);
    mql.addEventListener?.("change", onModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      mql.removeEventListener?.("change", onModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return "unavailable" as const;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === "accepted") setInstalled(true);
    return choice.outcome;
  }, [deferred]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }, []);

  return {
    canPrompt: Boolean(deferred),
    isIos: isIosSafari(),
    isInstalled: installed,
    dismissed,
    promptInstall,
    dismiss,
  };
}
